import { type ReactNode, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import type { StyleProp, ViewStyle } from "react-native";

import { colors, radii, spacing } from "../../../../styles/design-system";
import Tooltip from "../../../common/tooltip/Tooltip";

export interface MessageActionButtonProps {
  tooltipContent?: string | null;
  accessibilityLabel: string;
  accessibilityHint?: string;
  onPress: () => void;
  icon: (isInteracting: boolean) => ReactNode;
  isActive?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  baseStyle?: StyleProp<ViewStyle>;
  hoverStyle?: StyleProp<ViewStyle>;
  activeStyle?: StyleProp<ViewStyle>;
  activeHoverStyle?: StyleProp<ViewStyle>;
  disabledStyle?: StyleProp<ViewStyle>;
}

export const MessageActionButton = ({
  tooltipContent,
  accessibilityLabel,
  accessibilityHint,
  onPress,
  icon,
  isActive = false,
  isSelected = false,
  isDisabled = false,
  baseStyle,
  hoverStyle,
  activeStyle,
  activeHoverStyle,
  disabledStyle,
}: MessageActionButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const button = (
    <Pressable
      style={(state) => {
        const isInteracting = isHovered || state.pressed;
        return [
          styles.actionBtn,
          baseStyle,
          !isActive && isInteracting && (hoverStyle ?? styles.actionBtnHover),
          isActive && activeStyle,
          isActive && isInteracting && activeHoverStyle,
          isDisabled && disabledStyle,
        ];
      }}
      onPress={onPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      disabled={isDisabled}
      role="button"
      aria-label={accessibilityLabel}
      aria-pressed={isSelected}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ selected: isSelected, disabled: isDisabled }}>
      <View accessibilityElementsHidden accessible={false} importantForAccessibility="no">
        {icon(isHovered)}
      </View>
    </Pressable>
  );

  if (!tooltipContent) {
    return button;
  }

  return (
    <Tooltip content={tooltipContent} placement="bottom" offset={8} showArrow={false}>
      {button}
    </Tooltip>
  );
};

const styles = StyleSheet.create({
  actionBtn: {
    padding: spacing.xs,
    borderRadius: radii.md,
  },
  actionBtnHover: {
    backgroundColor: colors.grey[100],
  },
});
